import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';
import { Kelas, TahunAjaran, PaginatedResult } from '../types';
import { TableSkeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { Modal } from '../components/ui/Modal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Pagination } from '../components/ui/Pagination';
import { Edit2, Trash2, Search, Building2, Users } from 'lucide-react';
import { useTingkat } from '../lib/tingkat';

export function KelasPage() {
  const { hasPermission } = useAuth();
  const { success, error } = useToast();
  const queryClient = useQueryClient();
  const { tingkatList: TINGKAT_OPTIONS } = useTingkat();

  const canEdit = hasPermission('kelas', 'ubah');
  const canDelete = hasPermission('kelas', 'hapus');

  // Filter & Pagination State
  const [tingkatFilter, setTingkatFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Modal State (hanya untuk mengubah kelas yang sudah ada)
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Kelas | null>(null);
  const [formData, setFormData] = useState({
    tahun_ajaran_id: 1,
    wali_kelas_id: null as number | null,
    kapasitas: 32,
  });

  // Delete State
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // Queries
  const { data: kelasData, isLoading } = useQuery({
    queryKey: ['kelas', tingkatFilter, searchTerm, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (tingkatFilter) params.append('tingkat', tingkatFilter);
      if (searchTerm) params.append('search', searchTerm);

      const res = await api.get<PaginatedResult<Kelas>>(`/api/kelas?${params.toString()}`);
      return res.data;
    },
  });

  const { data: guruSimple = [] } = useQuery({
    queryKey: ['guru-simple'],
    queryFn: async () => {
      const res = await api.get<any[]>('/api/guru/simple');
      return res.data || [];
    },
  });

  const { data: taList = [] } = useQuery({
    queryKey: ['tahun-ajaran'],
    queryFn: async () => {
      const res = await api.get<TahunAjaran[]>('/api/tahun-ajaran');
      return res.data || [];
    },
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['kelas'] });
    queryClient.invalidateQueries({ queryKey: ['kelas-all'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
  };

  // Mutations
  const updateMutation = useMutation({
    mutationFn: ({ item, data }: { item: Kelas; data: typeof formData }) =>
      api.put(`/api/kelas/${item.id}`, {
        // Nama & tingkat dibuat di menu Tingkat Kelas, dikirim apa adanya
        tingkat: String(item.tingkat),
        nama: item.nama,
        tahun_ajaran_id: data.tahun_ajaran_id,
        wali_kelas_id: data.wali_kelas_id,
        kapasitas: data.kapasitas,
      }),
    onSuccess: () => {
      refresh();
      success('Kelas berhasil diperbarui.');
      setModalOpen(false);
    },
    onError: (err: any) => error(err.message || 'Gagal memperbarui kelas.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/api/kelas/${id}`),
    onSuccess: () => {
      refresh();
      success('Kelas berhasil dihapus.');
      setDeleteId(null);
    },
    onError: (err: any) => {
      error(err.message || 'Gagal menghapus kelas.');
      setDeleteId(null);
    },
  });

  const handleOpenModal = (item: Kelas) => {
    setEditingItem(item);
    setFormData({
      tahun_ajaran_id: item.tahun_ajaran_id,
      wali_kelas_id: item.wali_kelas_id,
      kapasitas: item.kapasitas,
    });
    setModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem) return;
    updateMutation.mutate({ item: editingItem, data: formData });
  };

  const tingkatLabel = (tingkat: string | number) =>
    TINGKAT_OPTIONS.find((t) => t.value === String(tingkat))?.label || `Kelas ${tingkat}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-800">Kelas & Rombongan Belajar</h2>
        <p className="text-xs text-slate-500 mt-1">
          Nama kelas dan rombel dibuat di menu Tingkat Kelas. Di sini mengatur wali kelas, tahun
          ajaran, dan kapasitas.
        </p>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col sm:flex-row gap-3 items-center justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            placeholder="Cari nama kelas..."
            className="w-full pl-9 pr-4 py-2 text-xs border border-slate-200 rounded-xl focus:outline-emerald-600 bg-slate-50/50"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <select
            value={tingkatFilter}
            onChange={(e) => {
              setTingkatFilter(e.target.value);
              setPage(1);
            }}
            className="w-full sm:w-auto px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-emerald-600 bg-white"
          >
            <option value="">Semua Tingkat</option>
            {TINGKAT_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
        {isLoading ? (
          <div className="p-6">
            <TableSkeleton rows={4} cols={6} />
          </div>
        ) : !kelasData || kelasData.data.length === 0 ? (
          <EmptyState
            title="Belum Ada Kelas"
            description="Buat tingkat beserta rombelnya di menu Tingkat Kelas, kelasnya akan muncul di sini."
          />
        ) : (
          <div>
            <div className="overflow-x-auto">
                <table className="w-full text-left text-[10px] sm:text-xs">
  <thead className="bg-slate-50 border-b border-slate-200/80 text-slate-600 font-semibold uppercase tracking-tight sm:tracking-wider text-[9px] sm:text-[11px]">
    <tr>
      <th className="py-3 px-1.5 pl-4 sm:py-3.5 sm:px-4">Nama Kelas</th>
      <th className="py-3 px-1.5 sm:py-3.5 sm:px-4 text-center">Tingkat</th>
      <th className="py-3 px-1.5 sm:py-3.5 sm:px-4">Wali Kelas</th>
      <th className="py-3 px-1.5 sm:py-3.5 sm:px-4">Tahun Ajaran</th>
      <th className="py-3 px-1.5 sm:py-3.5 sm:px-4 text-center">
  <span className="sm:hidden">Siswa</span>
  <span className="hidden sm:inline">Siswa / Kapasitas</span>
</th>
      <th className="py-3 px-2 sm:py-3.5 sm:px-4 text-center">Aksi</th>
    </tr>
  </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {kelasData.data.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50/70 transition-colors">
                      <td className="py-2.5 px-1.5 pl-4 sm:py-3.5 sm:px-4 font-bold text-slate-900 whitespace-nowrap">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <Building2 className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600 shrink-0" />
                          <span>{item.nama}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-1.5 sm:py-3.5 sm:px-4 text-center">
  <span className="inline-block px-1.5 sm:px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-800 font-semibold whitespace-nowrap">
    {tingkatLabel(item.tingkat)}
  </span>
</td>
                      <td className="py-2.5 px-1.5 sm:py-3.5 sm:px-4 font-medium text-slate-800 leading-snug">
                        {item.wali_kelas ? (
                          <span>
                            {item.wali_kelas.gelar_depan ? `${item.wali_kelas.gelar_depan} ` : ''}
                            {item.wali_kelas.nama}
                            {item.wali_kelas.gelar_belakang ? `, ${item.wali_kelas.gelar_belakang}` : ''}
                          </span>
                        ) : (
                          <span className="text-slate-400 italic">Belum ditentukan</span>
                        )}
                      </td>
                      <td className="py-2.5 px-1.5 sm:py-3.5 sm:px-4 text-slate-500 leading-snug">
                        {item.tahun_ajaran?.tahun ? (
                          <>
                            <span className="block sm:inline">{item.tahun_ajaran.tahun}</span>
                            <span className="block sm:inline sm:ml-1">
                              ({item.tahun_ajaran.semester || '-'})
                            </span>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="py-2.5 px-1.5 sm:py-3.5 sm:px-4 whitespace-nowrap">
                        <div className="flex items-center gap-1 sm:gap-1.5">
                          <Users className="hidden sm:block w-3.5 h-3.5 text-slate-400" />
                          <span className="font-semibold text-slate-800">
                            {item.total_siswa || 0}
                          </span>
                          <span className="text-slate-400">
                            / {item.kapasitas}
                            <span className="hidden sm:inline"> siswa</span>
                          </span>
                        </div>
                      </td>
                      <td className="py-2.5 px-2 sm:py-3.5 sm:px-4 text-center">
  <div className="flex items-center justify-center gap-0 sm:gap-1.5">
                          {canEdit && (
                            <button
                              onClick={() => handleOpenModal(item)}
                              className="p-1 sm:p-1.5 text-slate-500 hover:text-emerald-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                              title="Atur wali kelas, tahun ajaran, kapasitas"
                            >
                              <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => setDeleteId(item.id)}
                              className="p-1 sm:p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                              title="Hapus"
                            >
                              <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={kelasData.page}
              totalPages={kelasData.totalPages}
              totalItems={kelasData.total}
              itemsPerPage={kelasData.limit}
              onPageChange={(p) => setPage(p)}
              onLimitChange={(l) => {
                setLimit(l);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Atur Rombongan Belajar"
        maxWidth="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {editingItem && (
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
              <p className="text-[10px] text-slate-400">Kelas</p>
              <p className="text-sm font-bold text-slate-900">{editingItem.nama}</p>
              <p className="text-[11px] text-slate-500">{tingkatLabel(editingItem.tingkat)}</p>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Tahun Ajaran
            </label>
            <select
              value={formData.tahun_ajaran_id}
              onChange={(e) =>
                setFormData({ ...formData, tahun_ajaran_id: Number(e.target.value) })
              }
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:outline-emerald-600 bg-white"
              required
            >
              {taList.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.tahun} ({t.semester}) {t.is_active ? '— [AKTIF]' : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Wali Kelas (Opsional)
            </label>
            <select
              value={formData.wali_kelas_id || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  wali_kelas_id: e.target.value ? Number(e.target.value) : null,
                })
              }
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:outline-emerald-600 bg-white"
            >
              <option value="">-- Pilih Guru Wali Kelas --</option>
              {guruSimple.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.nama} ({g.jabatan})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Kapasitas Maksimal Siswa
            </label>
            <input
              type="number"
              min={1}
              max={60}
              value={formData.kapasitas}
              onChange={(e) => setFormData({ ...formData, kapasitas: Number(e.target.value) })}
              className="w-full px-3.5 py-2 text-sm border border-slate-300 rounded-xl focus:outline-emerald-600"
              required
            />
          </div>

          <div className="pt-4 flex items-center justify-end gap-2 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-xl"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="px-4 py-2 text-xs font-semibold text-white bg-emerald-700 hover:bg-emerald-800 disabled:bg-emerald-400 rounded-xl transition-colors cursor-pointer"
            >
              Simpan Perubahan
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        title="Hapus Rombel Kelas"
        message="Apakah Anda yakin ingin menghapus kelas ini? Kelas yang masih memiliki siswa tidak dapat dihapus."
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}